import { useCallback } from "react";

import { AccountOnHoldCheckoutMessage } from "@/components/AccountOnHold";
import { useAppState } from "@/components/AppStateProvider";
import { SupplementsProvider } from "@/contexts/SupplementContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { useShopPermissions } from "@/hooks/useShopPermissions";
import useUserProfile from "@/hooks/useUserProfile";
import { PanelBoxV2 } from "@/tpo/Boxes";
import ListPageTemplate from "@/tpo/shop/ListPageTemplate";
import {
  SupplementControls,
  SupplementDetailModal,
  SupplementList
} from "@/tpo/shop/SupplementListPage";
import { useTheme } from "styled-components";

function CmpSupplementList() {
  const { organisation } = useOrganisationContext();
  const { practitionerProfile } = useUserProfile();
  const { addSupplementToBasket } = useOrganisationBasketContext();
  const { setPractitionerBasketOpen: setBasketOpen } = useAppState();
  const { canPlaceOrders } = useShopPermissions();

  const showReportsTab = practitionerProfile?.showReportsTab;

  const onAddToBasket = useCallback(
    ({ supplement }) => {
      addSupplementToBasket(supplement.id);
      setBasketOpen(true);
    },
    [addSupplementToBasket, setBasketOpen]
  );

  const theme = useTheme();

  return (
    <>
      <ListPageTemplate
        bg="haze"
        switcherOptions={[
          {
            label: "tests",
            value: "tests"
          },
          ...(showReportsTab ? [{ label: "reports", value: "reports" }] : []),
          {
            label: "supplements",
            value: "supplements"
          },
          {
            label: "discounts",
            value: "discounts"
          }
        ]}
        urlMap={{
          supplements: `/cmp/organisations/${organisation.hashId}/dashboard/shop/supplements`,
          tests: `/cmp/organisations/${organisation.hashId}/dashboard/shop/tests`,
          ...(showReportsTab
            ? { reports: `/cmp/organisations/${organisation.hashId}/dashboard/shop/reports` }
            : {}),
          discounts: `/cmp/organisations/${organisation.hashId}/dashboard/shop/discounts`
        }}
        tab="supplements"
        title="Find a matching supplement"
        subtitle="We can get a huge amount of benefit from small changes to our diets. Alongside our nutritional advice we offer more specific, targeted support, in the form of carefully selected supplements. These supplements have been designed to be specific rather than general."
        pb={[30, 30, 60]}
        centerProps={{
          stacked: true
        }}
        aboveSwitcher={
          organisation?.accountOnHold ? (
            <AccountOnHoldCheckoutMessage withGreyOuterBox={false} maxWidth={640} mb={60} />
          ) : null
        }
      >
        <PanelBoxV2
          maxWidth={1220}
          outer={{
            bg: "haze",
            pb: theme.spacing.section.pb,
            px: [20, 20, 40]
          }}
        >
          <SupplementControls />
          <SupplementList onAddToBasket={onAddToBasket} disabled={!canPlaceOrders} />
        </PanelBoxV2>
      </ListPageTemplate>
      <SupplementDetailModal onAddToBasket={onAddToBasket} disabled={!canPlaceOrders} />
    </>
  );
}

export default function SupplementListPage() {
  return (
    <SupplementsProvider>
      <CmpSupplementList />
    </SupplementsProvider>
  );
}
